/**
 * Search every thing Denker remembers (spec §7.3 Search): the matching
 * things, paged, with an empty line, a retry row and a spinner. Debounced
 * while someone types. Shared by the memory pocket's root and the wall's
 * memory tile, which each decide what a row opens and what "nothing" says.
 */
import { searchThings, type MemoryThing } from "@/api/memory-focus";
import { PocketRow, PocketSection, Spinner } from "@/components/ui";
import { usePagedQuery } from "@/hooks/use-paged-query";
import { useT } from "@/i18n";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import { thingDisplayName, thingKindLine } from "@/lib/memory-thing-kinds";
import { ThingAvatar } from "./thing-avatar";

export function MemorySearchResults({
  query,
  youName,
  emptyText,
  onOpen,
}: {
  /** The trimmed query; empty shows nothing. */
  query: string;
  youName: string;
  emptyText: string;
  onOpen: (thing: MemoryThing) => void;
}) {
  const t = useT(framesMemoryMessages);
  const results = usePagedQuery<MemoryThing>(query || null, (cursor) =>
    searchThings(query, cursor),
  );
  if (!query) return null;
  return (
    <>
      {results.items.length > 0 ? (
        <PocketSection testId="memory-search-results">
          {results.items.map((thing) => (
            <PocketRow
              key={thing.id ?? thing.name}
              testId={`memory-search-result-${thing.id}`}
              leading={
                <ThingAvatar
                  kind={thing.kind}
                  name={thing.kind === "user_profile" ? youName : thing.name}
                />
              }
              title={thingDisplayName(thing)}
              secondLine={thingKindLine(thing, youName)}
              onSelect={() => onOpen(thing)}
            />
          ))}
          {results.hasMore ? (
            <PocketRow
              testId="memory-search-more"
              title={t("showMore")}
              onSelect={results.loadMore}
            />
          ) : null}
        </PocketSection>
      ) : null}
      {results.settled && !results.error && results.items.length === 0 ? (
        <p
          className="px-2 py-1 text-appkit-caption text-muted"
          data-testid="memory-search-empty"
        >
          {emptyText}
        </p>
      ) : null}
      {results.error ? (
        <PocketRow
          testId="memory-search-retry"
          title={t("searchFailed")}
          onSelect={results.retry}
        />
      ) : null}
      {results.loading && results.items.length === 0 ? (
        <div className="flex justify-center py-3">
          <Spinner className="h-4 w-4 text-muted" />
        </div>
      ) : null}
    </>
  );
}
